import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

interface Resource<T> {
    data: T | null;
    error: string | null;
    loading: boolean;
    reload: () => void;
}

/**
 * Loads a GET endpoint and re-runs when `path` changes.
 *
 * Errors are surfaced rather than swallowed — a page that silently renders an
 * empty table when the request failed is indistinguishable from one with no
 * data, and in a compliance product that difference matters.
 */
export const useResource = <T>(path: string | null): Resource<T> => {
    const [data, setData] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(path !== null);
    const [nonce, setNonce] = useState(0);

    const reload = useCallback(() => setNonce((n) => n + 1), []);

    useEffect(() => {
        if (path === null) {
            setLoading(false);
            return;
        }

        let live = true;
        setLoading(true);
        setError(null);

        api.get<T>(path)
            .then((result) => {
                if (live) setData(result);
            })
            .catch((err: unknown) => {
                if (live) setError(err instanceof Error ? err.message : "Could not load data");
            })
            .finally(() => {
                if (live) setLoading(false);
            });

        return () => {
            live = false;
        };
    }, [path, nonce]);

    return { data, error, loading, reload };
};

export const formatDate = (value: string | null | undefined): string => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const formatTime = (value: string): string =>
    new Date(value).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false });

export const formatDateTime = (value: string): string => `${formatDate(value)} ${formatTime(value)}`;

/** Days until a date, negative when already past. */
export const daysUntil = (value: string): number => {
    const then = new Date(value);
    then.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.round((then.getTime() - now.getTime()) / 86_400_000);
};

export const titleCase = (value: string): string =>
    value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
